import { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { Footer } from '../../components/Footer';
import { Navigation } from '../../components/Navigation';
import { useSeo } from '../../hooks/useSeo';
import { LEGAL_DATES, LEGAL_ROUTES } from './legal';
import './legal.css';

interface LegalLayoutProps {
  /** Page title, e.g. "Privacy Policy". */
  title: string;
  /** Short lead sentence under the title. */
  intro: ReactNode;
  /** Which legal page is active — highlights the correct cross-nav tab. */
  active: keyof typeof LEGAL_ROUTES;
  /** The policy body. */
  children: ReactNode;
}

const TABS: { key: keyof typeof LEGAL_ROUTES; label: string }[] = [
  { key: 'terms', label: 'Terms of Service' },
  { key: 'privacy', label: 'Privacy Policy' },
  { key: 'cookies', label: 'Cookie Policy' },
  { key: 'subprocessors', label: 'Sub-processors' },
];

export function LegalLayout({ title, intro, active, children }: LegalLayoutProps) {
  useSeo({
    title: `${title} — Revvly`,
    description: typeof intro === 'string' ? intro : `${title} for Revvly, the AI subscription revenue management platform.`,
    path: LEGAL_ROUTES[active],
    type: 'website',
  });

  return (
    <div className="legal">
      <Navigation />

      <main className="legal__main">
        <div className="legal__inner">
          <header className="legal__header">
            <p className="legal__eyebrow">Legal</p>
            <h1 className="legal__title">{title}</h1>
            <p className="legal__lead">{intro}</p>
            <p className="legal__dates">
              <span>
                Effective date: <strong>{LEGAL_DATES.effective}</strong>
              </span>
              <span aria-hidden className="legal__dot">
                ·
              </span>
              <span>
                Last updated: <strong>{LEGAL_DATES.lastUpdated}</strong>
              </span>
            </p>
          </header>

          <nav className="legal__tabs" aria-label="Legal documents">
            {TABS.map((tab) => (
              <NavLink
                key={tab.key}
                to={LEGAL_ROUTES[tab.key]}
                className={`legal__tab${active === tab.key ? ' legal__tab--active' : ''}`}
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>

          <article className="legal__body">{children}</article>
        </div>
      </main>

      <Footer />
    </div>
  );
}
